import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";

import { formatDateTime } from "./admin/shared";
import {
  fromDateInput,
  stepSeries,
  TeacherStepChart,
  toDateInput,
} from "./admin/TeacherStepChart";

import type {
  ReachedStep,
  SeriesUnit,
  SeriesView,
} from "./admin/TeacherStepChart";

import type { ApiErrorCode } from "../api/client";
import type { AdminTeacherActivity } from "@canvaschool/contracts";

type Funnel = AdminTeacherActivity["funnel"];
type TeacherRow = AdminTeacherActivity["teachers"][number];

const STEPS: Array<{ key: keyof Funnel; label: string }> = [
  { key: "teachers", label: "사용 중 교사 계정" },
  { key: "credentialsSent", label: "안내 메일 발송" },
  { key: "loggedIn", label: "첫 로그인" },
  { key: "passwordChanged", label: "비밀번호 변경" },
  { key: "classCreated", label: "학급 만듦" },
  { key: "studentsNamed", label: "학생 이름 등록" },
  { key: "canvasCreated", label: "캔버스 만듦" },
  { key: "studentsLoggedIn", label: "학생 로그인" },
  { key: "canvasEdited", label: "캔버스 작업 있음" },
  { key: "active7d", label: "최근 7일 활동" },
];

export { stepSeries };

type SortKey =
  | "name"
  | "stage"
  | "firstLogin"
  | "lastLogin"
  | "classes"
  | "students"
  | "canvases"
  | "studentLogin";
type SortDir = "asc" | "desc";

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: "name", label: "교사" },
  { key: "stage", label: "현재 단계" },
  { key: "firstLogin", label: "첫 로그인" },
  { key: "lastLogin", label: "마지막 로그인" },
  { key: "classes", label: "학급" },
  { key: "students", label: "학생(이름/로그인/전체)" },
  { key: "canvases", label: "캔버스(작업/전체)" },
  { key: "studentLogin", label: "학생 마지막 로그인" },
];

/** Stopped accounts sort lowest, then by how far along the funnel they are. */
const stageRank = (teacher: TeacherRow) => {
  if (teacher.status === "DISABLED") {
    return -1;
  }
  const done = [
    teacher.firstLoginAt !== null,
    teacher.passwordChanged,
    teacher.classes > 0,
    teacher.studentsNamed > 0,
    teacher.canvases > 0,
    teacher.studentsLoggedIn > 0,
    teacher.canvasesEdited > 0,
  ];
  const missing = done.indexOf(false);
  return missing === -1 ? done.length : missing;
};

const time = (value: string | null) =>
  value === null ? null : new Date(value).getTime();

/** Comparable parts per column; earlier parts decide first. */
const sortParts = (
  teacher: TeacherRow,
  key: SortKey,
): Array<number | string | null> => {
  switch (key) {
    case "name":
      return [teacher.name ?? "", teacher.loginId];
    case "stage":
      return [stageRank(teacher)];
    case "firstLogin":
      return [time(teacher.firstLoginAt)];
    case "lastLogin":
      return [time(teacher.lastLoginAt)];
    case "studentLogin":
      return [time(teacher.lastStudentLoginAt)];
    case "classes":
      return [teacher.classes];
    case "students":
      return [
        teacher.studentsNamed,
        teacher.studentsLoggedIn,
        teacher.students,
      ];
    case "canvases":
      return [teacher.canvasesEdited, teacher.canvases];
  }
};

/** Sorted copy; missing times go last in both directions. */
export const sortTeachers = (
  teachers: TeacherRow[],
  key: SortKey,
  dir: SortDir,
): TeacherRow[] => {
  const sign = dir === "asc" ? 1 : -1;
  return teachers
    .map((teacher, index) => ({
      teacher,
      index,
      parts: sortParts(teacher, key),
    }))
    .sort((a, b) => {
      for (let i = 0; i < a.parts.length; i++) {
        const x = a.parts[i];
        const y = b.parts[i];
        if (x === y) {
          continue;
        }
        if (x === null) {
          return 1;
        }
        if (y === null) {
          return -1;
        }
        const order =
          typeof x === "string" && typeof y === "string"
            ? x.localeCompare(y, "ko")
            : (x as number) - (y as number);
        if (order !== 0) {
          return order * sign;
        }
      }
      return a.index - b.index;
    })
    .map(({ teacher }) => teacher);
};

const CHART_STEPS = STEPS.filter(
  (step): step is { key: ReachedStep; label: string } =>
    step.key !== "active7d",
);

const UNITS: Array<{ key: SeriesUnit; label: string }> = [
  { key: "day", label: "일" },
  { key: "week", label: "주" },
  { key: "month", label: "월" },
];

/** Default range ending on `today`: 30 days, 12 weeks or 12 months. */
const defaultRange = (unit: SeriesUnit, today: Date) => {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  const start =
    unit === "day"
      ? new Date(y, m, d - 29)
      : unit === "week"
      ? new Date(y, m, d - 7 * 11)
      : new Date(y, m - 11, 1);
  return { start: toDateInput(start), end: toDateInput(today) };
};

/** Mail sent but no sign-in for this long is flagged in the stage column. */
const STALE_LOGIN_MS = 3 * 24 * 60 * 60 * 1000;

/** The first funnel step this teacher has not reached, as "○○ 전". */
export const currentStage = (
  teacher: TeacherRow,
  now: number = Date.now(),
): { text: string; stale: boolean } => {
  if (teacher.status === "DISABLED") {
    return { text: "사용 중지", stale: false };
  }
  // The mail step is skipped: it is the operator's step, and accounts made by
  // hand have no mail time at all. A missing first login is what matters.
  const steps: Array<[boolean, string]> = [
    [teacher.firstLoginAt !== null, "첫 로그인 전"],
    [teacher.passwordChanged, "비밀번호 변경 전"],
    [teacher.classes > 0, "학급 만들기 전"],
    [teacher.studentsNamed > 0, "학생 이름 등록 전"],
    [teacher.canvases > 0, "캔버스 만들기 전"],
    [teacher.studentsLoggedIn > 0, "학생 로그인 전"],
    [teacher.canvasesEdited > 0, "캔버스 작업 전"],
  ];
  const missing = steps.find(([done]) => !done);
  if (!missing) {
    return { text: "사용 중", stale: false };
  }
  const stale =
    teacher.firstLoginAt === null &&
    teacher.credentialsSentAt !== null &&
    now - new Date(teacher.credentialsSentAt).getTime() > STALE_LOGIN_MS;
  return { text: missing[1], stale };
};

const dateOrDash = (value: string | null) =>
  value ? formatDateTime(value) : "—";

/** Funnel steps as count, share of active teachers and a bar. */
export const TeacherFunnel = ({
  activity,
}: {
  activity: AdminTeacherActivity;
}) => {
  const total = activity.funnel.teachers;
  return (
    <ul className="canvaschool-list">
      {STEPS.map((step) => {
        const value = activity.funnel[step.key];
        const percent = total === 0 ? 0 : Math.round((value / total) * 100);
        return (
          <li key={step.key}>
            <span>
              {step.label} {value}명 ({percent}%)
            </span>
            <div
              aria-hidden="true"
              style={{
                height: "0.5rem",
                width: `${percent}%`,
                background: "var(--cs-warning)",
                borderRadius: "0.25rem",
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};

/** ACTIVE teachers grouped by the stage they stopped at, for the dashboard. */
export const stalledTeachers = (activity: AdminTeacherActivity) => {
  const now = new Date(activity.generatedAt).getTime();
  const name = (teacher: TeacherRow) =>
    teacher.name ? `${teacher.name}(${teacher.loginId})` : teacher.loginId;
  const staged = activity.teachers.map((teacher) => ({
    teacher,
    stage: currentStage(teacher, now),
  }));
  return {
    lateFirstLogin: staged
      .filter(({ stage }) => stage.stale)
      .map(({ teacher }) => name(teacher)),
    noClass: staged
      .filter(
        ({ teacher }) =>
          teacher.status === "ACTIVE" &&
          teacher.firstLoginAt !== null &&
          teacher.classes === 0,
      )
      .map(({ teacher }) => name(teacher)),
  };
};

/** Step, unit, range and view controls over the bar chart of one funnel step. */
const StepChartSection = ({ activity }: { activity: AdminTeacherActivity }) => {
  const today = useMemo(() => {
    const generated = new Date(activity.generatedAt);
    return new Date(
      generated.getFullYear(),
      generated.getMonth(),
      generated.getDate(),
    );
  }, [activity.generatedAt]);
  const [step, setStep] = useState<ReachedStep>("classCreated");
  const [unit, setUnit] = useState<SeriesUnit>("day");
  const [view, setView] = useState<SeriesView>("cumulative");
  const [range, setRange] = useState(() => defaultRange("day", today));

  const changeUnit = (next: SeriesUnit) => {
    setUnit(next);
    setRange(defaultRange(next, today));
  };
  // Keep start <= end whichever input the user just moved.
  const changeStart = (value: string) =>
    setRange((r) => ({ start: value, end: r.end < value ? value : r.end }));
  const changeEnd = (value: string) =>
    setRange((r) => ({ start: r.start > value ? value : r.start, end: value }));

  const stepInfo = CHART_STEPS.find((item) => item.key === step)!;
  const buckets = useMemo(() => {
    const start = fromDateInput(range.start);
    const end = fromDateInput(range.end);
    return start && end
      ? stepSeries(activity, step, unit, start, end, view)
      : [];
  }, [activity, step, unit, range, view]);
  const total = activity.funnel.teachers;
  const value = activity.funnel[step];
  const percent = total === 0 ? 0 : Math.round((value / total) * 100);

  return (
    <>
      <div className="canvaschool-step-controls">
        <label>
          항목
          <select
            value={step}
            onChange={(event) => setStep(event.target.value as ReachedStep)}
          >
            {CHART_STEPS.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label} ({activity.funnel[item.key]}명)
              </option>
            ))}
          </select>
        </label>
        <div className="canvaschool-step-group">
          단위
          <div className="canvaschool-step-segments">
            {UNITS.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={unit === item.key}
                onClick={() => changeUnit(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <label>
          시작일
          <input
            type="date"
            value={range.start}
            onChange={(event) =>
              event.target.value && changeStart(event.target.value)
            }
          />
        </label>
        <label>
          종료일
          <input
            type="date"
            value={range.end}
            onChange={(event) =>
              event.target.value && changeEnd(event.target.value)
            }
          />
        </label>
        <div className="canvaschool-step-group">
          보기
          <div className="canvaschool-step-segments">
            <button
              type="button"
              aria-pressed={view === "cumulative"}
              onClick={() => setView("cumulative")}
            >
              누적
            </button>
            <button
              type="button"
              aria-pressed={view === "new"}
              onClick={() => setView("new")}
            >
              새로 도달
            </button>
          </div>
        </div>
      </div>
      <p className="canvaschool-hint">
        오늘 {stepInfo.label} {value}명 (사용 중 교사 계정의 {percent}%)
      </p>
      <TeacherStepChart
        buckets={buckets}
        stepLabel={stepInfo.label}
        view={view}
      />
    </>
  );
};

/** Counts and sign-in times per teacher; class and canvas names are never sent. */
export const AdminTeacherActivityPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [activity, setActivity] = useState<AdminTeacherActivity | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const clickSort = (key: SortKey) =>
    setSort((current) =>
      current?.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  const rows = useMemo(
    () =>
      activity && sort
        ? sortTeachers(activity.teachers, sort.key, sort.dir)
        : activity?.teachers ?? [],
    [activity, sort],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setActivity(await api.getAdminTeacherActivity());
    } catch (failure) {
      setError(auth.handleError(failure));
    }
  }, [api, auth]);

  /** The shell follows the new teacher session to the teacher's home. */
  const enter = async (teacher: TeacherRow) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await auth.impersonate(teacher.id);
    } catch (failure) {
      setError(auth.handleError(failure));
      busyRef.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
    // Loading once per mount; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>교사 이용 현황</h1>
          <p className="canvaschool-page-lead">
            로그인 이후 학급 준비가 어디까지 됐는지 단계별로 셉니다. 학급과
            캔버스 내용은 표시되지 않습니다. 사용 중인 교사의 아이디를 누르면 그
            선생님의 화면으로 들어가 학급을 볼 수 있습니다.
          </p>
          {activity && activity.internalExcluded > 0 && (
            <p className="canvaschool-hint">
              내부 계정 {activity.internalExcluded}개는 모든 통계에서 뺐습니다.
            </p>
          )}
        </div>
        <button type="button" onClick={() => void load()}>
          새로고침
        </button>
      </div>
      {error && <ErrorAlert code={error} />}
      {activity && (
        <>
          <section aria-label="단계별 인원" className="canvaschool-todo">
            <h2>단계별 인원</h2>
            <StepChartSection activity={activity} />
            <TeacherFunnel activity={activity} />
          </section>
          {activity.teachers.length === 0 ? (
            <p className="canvaschool-hint">교사 계정이 없습니다.</p>
          ) : (
            <div className="canvaschool-table-wrap">
              <table className="canvaschool-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={
                          sort?.key === column.key
                            ? sort.dir === "asc"
                              ? "ascending"
                              : "descending"
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          className="canvaschool-sort-button"
                          onClick={() => clickSort(column.key)}
                        >
                          {column.label}
                          {sort?.key === column.key && (
                            <span aria-hidden="true">
                              {sort.dir === "asc" ? " ▲" : " ▼"}
                            </span>
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((teacher) => {
                    const stage = currentStage(
                      teacher,
                      new Date(activity.generatedAt).getTime(),
                    );
                    return (
                      <tr key={teacher.loginId}>
                        <td>
                          {[teacher.name, teacher.schoolName]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                          <br />
                          {teacher.status === "ACTIVE" ? (
                            <button
                              type="button"
                              className="canvaschool-link-button"
                              aria-label={`${teacher.loginId} 선생님 화면으로 들어가기`}
                              disabled={busy}
                              onClick={() => void enter(teacher)}
                            >
                              {teacher.loginId}
                            </button>
                          ) : (
                            <span className="canvaschool-hint">
                              {teacher.loginId}
                            </span>
                          )}
                        </td>
                        <td>
                          {stage.stale ? (
                            <strong className="canvaschool-error">
                              {stage.text} (3일 넘음)
                            </strong>
                          ) : (
                            stage.text
                          )}
                        </td>
                        <td>{dateOrDash(teacher.firstLoginAt)}</td>
                        <td>{dateOrDash(teacher.lastLoginAt)}</td>
                        <td>{teacher.classes}</td>
                        <td>
                          {teacher.studentsNamed}/{teacher.studentsLoggedIn}/
                          {teacher.students}
                        </td>
                        <td>
                          {teacher.canvasesEdited}/{teacher.canvases}
                        </td>
                        <td>{dateOrDash(teacher.lastStudentLoginAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};
